import { computed, type Readable } from '@vielzeug/ripple';

import { resolveMaybeReactive } from '../utils/dom';

/**
 * Produces a reactive string of class names from an object map.
 *
 * Each key is a class name. Its value may be:
 * - a static `boolean`
 * - a `Signal<boolean>`
 * - a getter `() => boolean`
 *
 * Returns a `Reactive<string>` that can be used directly in a template
 * class attribute:
 *
 * @example
 * ```ts
 * html`<div class="${classMap({ active: isActive, hidden: () => !isVisible.value })}"></div>`
 * ```
 */
export const classMap = (map: Record<string, (() => boolean) | Readable<boolean> | boolean>): Readable<string> => {
  return computed(() =>
    Object.entries(map)
      .filter(([, v]) => resolveMaybeReactive(v))
      // Strip whitespace from each key — spaces would inject extra class tokens.
      .map(([k]) => k.replace(/\s+/g, ''))
      .filter(Boolean)
      .join(' '),
  );
};
